import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.cafinc.ca';
const DEFAULT_IMAGE = '/og-image.jpg';

/**
 * SEO Component for dynamic meta tags
 * Usage: <SEO title="Page Title" description="Page description" />
 * 
 * Enhanced with:
 * - FAQ Schema support
 * - Product Schema support
 * - Service Schema support
 * - Optimized meta tags
 */
const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  schema = null,
  breadcrumbs = null,
  faqs = null,
  product = null,
  service = null,
  keywords = null,
}) => {
  const fullTitle = title 
    ? `${title} | CAF Group of Companies`
    : 'CAF Group of Companies | Fence & Property Services in Central Alberta';
  
  const defaultDescription = 'CAF Group of Companies - Central Alberta Fencing & Property Services. Quality fencing installation and commercial property maintenance in Red Deer County, Alberta.';
  const metaDescription = description || defaultDescription;
  
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : null;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  // Generate BreadcrumbList schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': crumb.name,
      'item': `${SITE_URL}${crumb.path}`,
    })),
  } : null;

  // Generate FAQ schema
  const faqSchema = faqs && faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  } : null;

  // Generate Product schema
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name,
    'description': product.description,
    'image': product.image ? (product.image.startsWith('http') ? product.image : `${SITE_URL}${product.image}`) : imageUrl,
    'brand': {
      '@type': 'Brand',
      'name': product.brand || 'Central Alberta Fencing',
    },
    'offers': product.price ? {
      '@type': 'Offer',
      'priceCurrency': 'CAD',
      'price': product.price,
      'availability': product.inStock !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      'seller': {
        '@type': 'Organization',
        'name': 'Central Alberta Fencing Inc',
      },
    } : undefined,
    'aggregateRating': product.rating ? {
      '@type': 'AggregateRating',
      'ratingValue': product.rating,
      'reviewCount': product.reviewCount || 1,
    } : undefined,
  } : null;

  // Generate Service schema
  const serviceSchema = service ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': service.type,
    'name': service.name,
    'description': service.description,
    'provider': {
      '@type': 'LocalBusiness',
      'name': service.provider || 'Central Alberta Fencing Inc',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': '116-25 Belich Crescent',
        'addressLocality': 'Red Deer County',
        'addressRegion': 'AB',
        'postalCode': 'T4E 1A5',
        'addressCountry': 'CA',
      },
    },
    'areaServed': {
      '@type': 'GeoCircle',
      'geoMidpoint': {
        '@type': 'GeoCoordinates',
        'latitude': 52.2681,
        'longitude': -113.8112,
      },
      'geoRadius': '80000',
    },
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="author" content="CAF Group of Companies" />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="CAF Group of Companies" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={imageUrl} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_CA" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Geographic Meta Tags */}
      <meta name="geo.region" content="CA-AB" />
      <meta name="geo.placename" content="Red Deer County" />
      <meta name="geo.position" content="52.2681;-113.8112" />
      <meta name="ICBM" content="52.2681, -113.8112" />
      
      {/* Mobile App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content="CAF Group" />
      
      {/* Schema.org Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {/* Product Schema */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {/* Service Schema */}
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
